import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import SendDoc from './pages/SendDoc';
import DocumentFieldEditor from './pages/DocumentFieldEditor';
import DocumentFieldEditorV2 from './pages/DocumentFieldEditorV2';
import Templates from './pages/Templates';
import TemplateDetail from './pages/TemplateDetail';
import TemplateFieldEditor from './pages/TemplateFieldEditor';
import Envelopes from './pages/Envelopes';
import EnvelopeDetail from './pages/EnvelopeDetail';
import SigningSession from './pages/SigningSession';
import NotaryPage from './pages/NotaryPage';
import WebhooksPage from './pages/WebhooksPage';
import AnalyticsPage from './pages/AnalyticsPage';
import VerifyPage from './pages/VerifyPage';
import ReviewAndSend from './pages/ReviewAndSend';
import PrepareEnvelope from './pages/PrepareEnvelope';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inactivity timeout configuration (in milliseconds)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // Show warning 5 minutes before logout

// Auth context
export const AuthContext = React.createContext();

function App() {
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [orgId, setOrgId] = useState(localStorage.getItem('org_id'));
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);

  // Refs for timers
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const login = (token, org) => {
    setAccessToken(token);
    setOrgId(org);
    localStorage.setItem('access_token', token);
    localStorage.setItem('org_id', org);
    localStorage.setItem('last_activity', Date.now().toString());
  };

  const logout = useCallback(() => {
    setAccessToken(null);
    setOrgId(null);
    setShowTimeoutWarning(false);
    localStorage.removeItem('access_token');
    localStorage.removeItem('org_id');
    localStorage.removeItem('last_activity');

    // Clear all timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  // Reset activity timer
  const resetActivityTimer = useCallback(() => {
    if (!accessToken) return;

    lastActivityRef.current = Date.now();
    localStorage.setItem('last_activity', Date.now().toString());
    setShowTimeoutWarning(false);

    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    // Set warning timer (fires 5 minutes before logout)
    warningTimerRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeoutCountdown(Math.floor(WARNING_BEFORE_LOGOUT / 1000));

      // Start countdown
      countdownIntervalRef.current = setInterval(() => {
        setTimeoutCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);

    // Set logout timer
    inactivityTimerRef.current = setTimeout(() => {
      console.log('Session expired due to inactivity');
      logout();
      // Redirect to landing page with message
      window.location.href = '/?session_expired=true';
    }, INACTIVITY_TIMEOUT);
  }, [accessToken, logout]);

  // Handle user staying logged in
  const handleStayLoggedIn = useCallback(() => {
    setShowTimeoutWarning(false);
    resetActivityTimer();
  }, [resetActivityTimer]);

  // Set up activity tracking when user is logged in
  useEffect(() => {
    if (!accessToken) return;

    // Check if session has already expired (e.g., user returns to tab after long time)
    const lastActivity = parseInt(localStorage.getItem('last_activity') || '0');
    const timeSinceLastActivity = Date.now() - lastActivity;

    if (lastActivity && timeSinceLastActivity > INACTIVITY_TIMEOUT) {
      console.log('Session expired while away');
      logout();
      window.location.href = '/?session_expired=true';
      return;
    }

    // Activity events to track
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity updates to avoid excessive timer resets
    let lastUpdate = 0;
    const throttleMs = 30000; // Only reset timer every 30 seconds of activity

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > throttleMs) {
        lastUpdate = now;
        resetActivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timer
    resetActivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [accessToken, logout, resetActivityTimer]);

  // Format countdown for display
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AuthContext.Provider value={{ accessToken, orgId, login, logout, API, resetActivityTimer }}>
      <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={accessToken ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/send" element={accessToken ? <SendDoc /> : <Navigate to="/" />} />
            <Route path="/send/editor" element={accessToken ? <DocumentFieldEditorV2 /> : <Navigate to="/" />} />
            <Route path="/send/editor/old" element={accessToken ? <DocumentFieldEditor /> : <Navigate to="/" />} />
            <Route path="/templates" element={accessToken ? <Templates /> : <Navigate to="/" />} />
            <Route path="/templates/:id" element={accessToken ? <TemplateDetail /> : <Navigate to="/" />} />
            <Route path="/templates/:id/editor" element={accessToken ? <TemplateFieldEditor /> : <Navigate to="/" />} />
            <Route path="/envelopes" element={accessToken ? <Envelopes /> : <Navigate to="/" />} />
            <Route path="/envelopes/:id" element={accessToken ? <EnvelopeDetail /> : <Navigate to="/" />} />
            <Route path="/envelopes/:envelopeId/prepare" element={accessToken ? <PrepareEnvelope /> : <Navigate to="/" />} />
            <Route path="/envelopes/:envelopeId/review" element={accessToken ? <ReviewAndSend /> : <Navigate to="/" />} />
            <Route path="/notary" element={accessToken ? <NotaryPage /> : <Navigate to="/" />} />
            <Route path="/webhooks" element={accessToken ? <WebhooksPage /> : <Navigate to="/" />} />
            <Route path="/analytics" element={accessToken ? <AnalyticsPage /> : <Navigate to="/" />} />

            {/* Public routes */}
            <Route path="/sign/:token" element={<SigningSession />} />
            <Route path="/verify/:envelopeId" element={<VerifyPage />} />
          </Routes>
      </BrowserRouter>

      {/* Session Timeout Warning Modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-xl font-bold text-white">Session Timeout Warning</h2>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Your session will expire due to inactivity. You will be automatically logged out in:
              </p>
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-amber-600">{formatCountdown(timeoutCountdown)}</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Click "Stay Logged In" to continue your session, or "Logout Now" to end your session immediately.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleStayLoggedIn}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={logout}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                >
                  Logout Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export default App;